import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { WebformService } from '@app/services/webform.service';
import { Subscription } from 'rxjs';

type ComplianceValue = {
  accepted?: boolean | number | string;
  contribute?: boolean | number | string;
};

@Component({
  standalone: false,
  selector: 'webform-compliance',
  templateUrl: './compliance.component.html',
  styleUrls: ['./compliance.component.scss'],
})
export class ComplianceComponent implements OnInit, OnDestroy {
  @Input() element: Record<string, unknown> = {};
  @Input() parentGroup!: FormGroup;

  readonly context = 'webform_compliance';
  readonly acceptedControl = new FormControl(false, {
    nonNullable: true,
    validators: Validators.requiredTrue,
  });

  readonly contribute = signal(false);
  #controlSub?: Subscription;
  #acceptedSub?: Subscription;
  #officialSub?: Subscription;
  #formDataSub?: Subscription;
  #wasOfficial = false;
  readonly #webform = inject(WebformService);

  get fieldId(): string {
    return this.getElementString('#webform_id');
  }

  get over300(): boolean {
    return this.isSelected(this.element['#invitation_over_300']);
  }

  get officialFieldId(): string {
    return this.getElementString('#official_field_id');
  }

  get officialFieldValue(): string {
    return this.getElementString('#official_field_value');
  }

  get isOfficial(): boolean {
    return (
      this.officialFieldId !== '' &&
      this.officialFieldValue !== '' &&
      this.#webform.matchesValue(this.officialValue, this.officialFieldValue)
    );
  }

  get contributeRequired(): boolean {
    return this.isOfficial && this.over300;
  }

  get title(): string {
    return this.getElementString('#admin_title');
  }

  get text(): string {
    return this.getElementString('#guidelines_text');
  }

  get cost(): string {
    return this.getElementString('#cost_title');
  }

  get accept(): string {
    return this.getElementString('#accepted__title');
  }

  get requiredError(): string {
    return this.getElementString('#accepted__required_error');
  }

  get fieldControl(): AbstractControl | null {
    return this.parentGroup?.get(this.fieldId) ?? null;
  }

  get showRequiredError(): boolean {
    return (
      this.acceptedControl.invalid &&
      (this.acceptedControl.touched || !!this.fieldControl?.touched)
    );
  }

  ngOnInit(): void {
    this.setValue(this.fieldControl?.value);
    this.updateRequirements();
    this.#controlSub = this.fieldControl?.valueChanges.subscribe((value) =>
      this.setValue(value)
    );
    this.#officialSub = this.parentGroup
      ?.get(this.webformFieldId(this.officialFieldId))
      ?.valueChanges.subscribe(() => this.updateRequirements());
    this.#formDataSub = this.#webform.formData.subscribe(() => this.updateRequirements());
    this.#acceptedSub = this.acceptedControl.valueChanges.subscribe(() =>
      this.updateValue()
    );
  }

  onContributeChange(contribute: boolean): void {
    this.contribute.set(contribute);
    this.updateValue(false);
  }

  ngOnDestroy(): void {
    this.#controlSub?.unsubscribe();
    this.#acceptedSub?.unsubscribe();
    this.#officialSub?.unsubscribe();
    this.#formDataSub?.unsubscribe();
  }

  private setValue(value: unknown): void {
    const complianceValue = this.getComplianceValue(value);

    this.acceptedControl.setValue(this.isSelected(complianceValue.accepted ?? value), {
      emitEvent: false,
    });
    this.contribute.set(this.isSelected(complianceValue.contribute));
  }

  private updateValue(markAsTouched: boolean = true): void {
    this.fieldControl?.setValue({
      accepted: this.acceptedControl.value === true,
      contribute: this.contribute(),
    });
    if (markAsTouched) this.fieldControl?.markAsTouched();
  }

  private updateRequirements(): void {
    const isOfficial = this.isOfficial;
    this.acceptedControl.setValidators(Validators.requiredTrue);
    this.acceptedControl.updateValueAndValidity({ emitEvent: false });
    this.fieldControl?.updateValueAndValidity({ emitEvent: false });

    if (this.contributeRequired && !this.contribute()) {
      this.contribute.set(true);
      this.updateValue(false);
    } else if (this.#wasOfficial && !isOfficial && this.contribute()) {
      this.contribute.set(false);
      this.updateValue(false);
    }

    this.#wasOfficial = isOfficial;
  }

  private getElementString(key: string): string {
    const value = this.element[key];
    return typeof value === 'string' ? value : '';
  }

  private get officialValue(): unknown {
    const control = this.parentGroup?.get(this.webformFieldId(this.officialFieldId));
    return control ? control.value : this.#webform.getFormDataValue(this.officialFieldId);
  }

  private webformFieldId(field: string): string {
    const separatorIndex = this.fieldId.indexOf('--');
    return separatorIndex === -1
      ? field
      : `${this.fieldId.slice(0, separatorIndex + 2)}${field}`;
  }

  private getComplianceValue(value: unknown): ComplianceValue {
    return typeof value === 'object' && value !== null ? (value as ComplianceValue) : {};
  }

  private isSelected(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
  }
}
